import { useState, useEffect, useCallback } from "react";

type UseComponentDimensionsType = {
  initialHeight?: number;
  initialWidth?: number;
  componentId: string;
  measureWhenReady?: boolean;
};

// Returns a component's dimensions during window resize events
export function useComponentDimensions({
  initialWidth = undefined,
  initialHeight = undefined,
  componentId,
  measureWhenReady,
}: UseComponentDimensionsType) {
  const [dynamicWidth, setDynamicWidth] = useState(initialWidth);
  const [dynamicHeight, setDynamicHeight] = useState(initialHeight);

  const updateComponentHeight = useCallback(() => {
    const element = document.querySelector(componentId);

    if (element) {
      const rect = element.getBoundingClientRect();
      const height = rect?.height; // Includes padding and borders
      const width = rect?.width;
      setDynamicHeight(height);
      setDynamicWidth(width);
    }
  }, [componentId]);

  useEffect(() => {
    updateComponentHeight();
    window.addEventListener("resize", updateComponentHeight);

    return () => {
      window.removeEventListener("resize", updateComponentHeight);
    };
  }, [updateComponentHeight]);

  // Re-measure once the caller reports its content has actually finished
  // loading, rather than guessing from a timer.
  useEffect(() => {
    if (measureWhenReady) updateComponentHeight();
  }, [measureWhenReady, updateComponentHeight]);

  return { dynamicHeight, dynamicWidth };
}
